import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { ConnectivityProvider } from "@/contexts/ConnectivityContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import GestoresPage from "@/pages/GestoresPage";
import VentasPage from "@/pages/VentasPage";
import MetasPage from "@/pages/MetasPage";
import MisVentasPage from "@/pages/MisVentasPage";
import ContabilidadPage from "@/pages/ContabilidadPage";
import RecompensasPage from "@/pages/RecompensasPage";
import PasajerosPage from "@/pages/PasajerosPage";
import WalletPage from "@/pages/WalletPage";
import NotificationsPage from "@/pages/NotificationsPage";
import EmailSettingsPage from "@/pages/EmailSettingsPage";
import Layout from "@/components/Layout";

// Client Portal
import ClientLoginPage from "@/pages/ClientLoginPage";
import ClientLayout from "@/components/ClientLayout";
import ClientDashboardPage from "@/pages/ClientDashboardPage";
import ClientWalletPage from "@/pages/ClientWalletPage";
import ClientServicesPage from "@/pages/ClientServicesPage";
import ClientCartPage from "@/pages/ClientCartPage";
import ClientOrdersPage from "@/pages/ClientOrdersPage";
import ClientDivisasPage from "@/pages/ClientDivisasPage";
import ClientHistoryPage from "@/pages/ClientHistoryPage";
import ClientReferralsPage from "@/pages/ClientReferralsPage";
import ClientSecurityPage from "@/pages/ClientSecurityPage";
import ClientFaqPage from "@/pages/ClientFaqPage";
import ClientTermsPage from "@/pages/ClientTermsPage";

// Admin Portal Management
import AdminPaymentMethodsPage from "@/pages/AdminPaymentMethodsPage";
import AdminServicesCatalogPage from "@/pages/AdminServicesCatalogPage";
import AdminExchangeRatesPage from "@/pages/AdminExchangeRatesPage";
import AdminClientOrdersPage from "@/pages/AdminClientOrdersPage";
import AdminWalletTopupsPage from "@/pages/AdminWalletTopupsPage";
import AdminClientsPage from "@/pages/AdminClientsPage";
import AdminMessagesPage from "@/pages/AdminMessagesPage";
import AdminAuditLogPage from "@/pages/AdminAuditLogPage";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import AdminDivisasPage from "@/pages/AdminDivisasPage";
import AdminB2BFeesPage from "@/pages/AdminB2BFeesPage";
import AdminB2BOrdersPage from "@/pages/AdminB2BOrdersPage";
import AdminB2BDashboard from "@/pages/AdminB2BDashboard";
import AdminRechargeConfigPage from "@/pages/AdminRechargeConfigPage";
import AdminAdministracionPage from "@/pages/AdminAdministracionPage";
import B2BTransfersPage from "@/pages/B2BTransfersPage";

// Workflow Engine
import WorkflowDashboardPage from "@/pages/WorkflowDashboardPage";
import ProvidersPage from "@/pages/ProvidersPage";
import QueuesPage from "@/pages/QueuesPage";

// CEO Access Hub
import AdminCentroAccesoPage from "@/pages/AdminCentroAccesoPage";
import MyWalletPage from "@/pages/wallets/MyWalletPage";
import OperationDetailPage from "@/pages/OperationDetailPage";
import RemittanceHistoryDetailedPage from "@/pages/RemittanceHistoryDetailedPage";
import ExchangeRatesPanel from "@/pages/ExchangeRatesPanel";
import DepositUsdtSettlementsPage from "@/pages/DepositUsdtSettlementsPage";
import MonthlySummaryPage from "@/pages/MonthlySummaryPage";
import HomeLandingPage from "@/pages/HomeLandingPage";
import ZonaDeCambiosPage from "@/pages/ZonaDeCambiosPage";
import CardBinManagementPage from "@/pages/admin/CardBinManagementPage";
import ServicePausePage from "@/pages/admin/ServicePausePage";
import AdminCleanupPage from "@/pages/admin/AdminCleanupPage";
import AdminGestoresRendicionPage from "@/pages/admin/AdminGestoresRendicionPage";
import GestorCuentaCorrientePage from "@/pages/remesas/GestorCuentaCorrientePage";

// Financial Engine (Admin read-only)
import FinanceLedgerExplorerPage from "@/pages/FinanceLedgerExplorerPage";
import FinanceTrialBalancePage from "@/pages/FinanceTrialBalancePage";
import FinanceSettlementsPage from "@/pages/FinanceSettlementsPage";
import FinanceReconciliationsPage from "@/pages/FinanceReconciliationsPage";
import FinanceSnapshotsPage from "@/pages/FinanceSnapshotsPage";

// Provider Portal
import ProviderLoginPage from "@/pages/ProviderLoginPage";
import ProviderLayout from "@/components/ProviderLayout";
import ProviderDashboardPage from "@/pages/ProviderDashboardPage";
import ProviderOperationsPage from "@/pages/ProviderOperationsPage";
import ProviderQueuesPage from "@/pages/ProviderQueuesPage";
import ProviderDocumentsPage from "@/pages/ProviderDocumentsPage";
import ProviderAccountPage from "@/pages/ProviderAccountPage";
import ProviderPayoutAccountPage from "@/pages/provider/ProviderPayoutAccountPage";
import ProviderPaymentMethodsPage from "@/pages/ProviderPaymentMethodsPage";
import AdminProviderPaymentMethodsPage from "@/pages/AdminProviderPaymentMethodsPage";

// Gestor Remesas Panel
import GestorLayout from "@/components/GestorLayout";
import { GestorRemesasInicioPage } from "@/pages/remesas/GestorRemesasInicioPage";
import GestorRemesasDashboardPage from "@/pages/remesas/GestorRemesasDashboardPage";
import AdminRemittanceDepositsPage from "@/pages/AdminRemittanceDepositsPage";
import AdminTreasuryUSDTPage from "@/pages/AdminTreasuryUSDTPage";
import GestorConversorPage from "@/pages/remesas/GestorConversorPage";
import GestorEnviarRemesaPage from "@/pages/remesas/GestorEnviarRemesaPage";
import { GestorPendientesPage, GestorRechazadasPage, GestorHistorialPage } from "@/pages/remesas/GestorRemesaListPages";
import GestorCuentasBancariasPage from "@/pages/remesas/GestorCuentasBancariasPage";
import {
  GestorPromotoresPage, GestorTrabajadoresPage, GestorUsuariosPage,
  GestorFaqPage, GestorSoportePage,
} from "@/pages/remesas/GestorRemesaShells";
import { GestorMisPasajerosPage, GestorMiWalletPage } from "@/pages/remesas/GestorPortalWrappers";
import GestorPreviewLaunchPage from "@/pages/GestorPreviewLaunchPage";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, isAdmin, isClient, isProvider, user } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner w-10 h-10"></div></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Route non-admin/gestor roles to their own portals before mounting the admin shell.
  if (isProvider && isProvider()) return <Navigate to="/provider" replace />;
  if (isClient && isClient()) return <Navigate to="/cliente" replace />;
  // Gestor must NEVER see admin layout. Send to their own panel.
  if (user?.role === 'gestor') return <Navigate to="/remesas" replace />;
  if (adminOnly && !isAdmin()) return <Navigate to="/remesas" replace />;
  return children;
};

const ClientRoute = ({ children }) => {
  const { isAuthenticated, loading, isClient } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner w-10 h-10"></div></div>;
  if (!isAuthenticated || !isClient()) return <Navigate to="/cliente/login" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <RealtimeProvider>
      <ConnectivityProvider>
      <BrowserRouter>
        <PwaInstallPrompt />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/cliente/login" element={<ClientLoginPage />} />
          <Route path="/provider/login" element={<ProviderLoginPage />} />
          {/* Admin → Gestor preview launcher (consumes short-lived JWT) */}
          <Route path="/preview/launch" element={<GestorPreviewLaunchPage />} />

          {/* Provider Portal */}
          <Route path="/provider" element={<ProviderLayout />}>
            <Route index element={<HomeLandingPage />} />
            <Route path="dashboard" element={<ProviderDashboardPage />} />
            <Route path="card-bins" element={<CardBinManagementPage />} />
            <Route path="pausa" element={<ServicePausePage />} />
            <Route path="operaciones" element={<ProviderOperationsPage />} />
            <Route path="queues" element={<ProviderQueuesPage />} />
            <Route path="documentos" element={<ProviderDocumentsPage />} />
            <Route path="metodos" element={<ProviderPaymentMethodsPage />} />
            <Route path="cuenta" element={<ProviderAccountPage />} />
            <Route path="wallet" element={<MyWalletPage />} />
            <Route path="datos-cobro" element={<ProviderPayoutAccountPage />} />
            <Route path="operations/:id" element={<OperationDetailPage />} />
            <Route path="remesas/historial-detallado" element={<RemittanceHistoryDetailedPage />} />
            <Route path="zona-de-cambios" element={<ZonaDeCambiosPage />} />
            <Route path="tasas-cambio" element={<ExchangeRatesPanel />} />
            <Route path="recargas-usdt" element={<DepositUsdtSettlementsPage />} />
          </Route>

          {/* Gestor Remesas Panel */}
          <Route path="/remesas" element={<GestorLayout />}>
            <Route index element={<HomeLandingPage />} />
            <Route path="inicio" element={<GestorRemesasInicioPage />} />
            <Route path="dashboard" element={<GestorRemesasDashboardPage />} />
            <Route path="conversor" element={<GestorConversorPage />} />
            <Route path="enviar" element={<GestorEnviarRemesaPage />} />
            <Route path="pendientes" element={<GestorPendientesPage />} />
            <Route path="rechazadas" element={<GestorRechazadasPage />} />
            <Route path="historial" element={<GestorHistorialPage />} />
            <Route path="cuentas" element={<GestorCuentasBancariasPage />} />
            <Route path="promotores" element={<GestorPromotoresPage />} />
            <Route path="trabajadores" element={<GestorTrabajadoresPage />} />
            <Route path="usuarios" element={<GestorUsuariosPage />} />
            <Route path="faq" element={<GestorFaqPage />} />
            <Route path="soporte" element={<GestorSoportePage />} />
            <Route path="mis-pasajeros" element={<GestorMisPasajerosPage />} />
            <Route path="mi-wallet" element={<GestorMiWalletPage />} />
            <Route path="wallet-ganancias" element={<MyWalletPage />} />
            <Route path="cuenta-corriente-ceo" element={<GestorCuentaCorrientePage />} />
            <Route path="historial-detallado" element={<RemittanceHistoryDetailedPage />} />
            <Route path="tasas-cambio" element={<ExchangeRatesPanel />} />
            <Route path="operations/:id" element={<OperationDetailPage />} />
          </Route>

          {/* Client Portal */}
          <Route path="/cliente" element={<ClientRoute><ClientLayout /></ClientRoute>}>
            <Route index element={<ClientDashboardPage />} />
            <Route path="wallet" element={<ClientWalletPage />} />
            <Route path="servicios" element={<ClientServicesPage />} />
            <Route path="carrito" element={<ClientCartPage />} />
            <Route path="ordenes" element={<ClientOrdersPage />} />
            <Route path="divisas" element={<ClientDivisasPage />} />
            <Route path="transferencias" element={<B2BTransfersPage />} />
            <Route path="historial" element={<ClientHistoryPage />} />
            <Route path="referidos" element={<ClientReferralsPage />} />
            <Route path="seguridad" element={<ClientSecurityPage />} />
            <Route path="ayuda" element={<ClientFaqPage />} />
            <Route path="terminos" element={<ClientTermsPage />} />
          </Route>

          {/* Admin/Gestor Panel */}
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<ProtectedRoute adminOnly><DashboardPage /></ProtectedRoute>} />
            <Route path="dashboard" element={<ProtectedRoute adminOnly><DashboardPage /></ProtectedRoute>} />
            <Route path="leaderboard" element={<ProtectedRoute adminOnly><LeaderboardPage /></ProtectedRoute>} />
            <Route path="gestores" element={<ProtectedRoute adminOnly><GestoresPage /></ProtectedRoute>} />
            <Route path="ventas" element={<ProtectedRoute adminOnly><VentasPage /></ProtectedRoute>} />
            <Route path="metas" element={<ProtectedRoute adminOnly><MetasPage /></ProtectedRoute>} />
            <Route path="contabilidad" element={<ProtectedRoute adminOnly><ContabilidadPage /></ProtectedRoute>} />
            <Route path="recompensas" element={<RecompensasPage />} />
            <Route path="mis-ventas" element={<MisVentasPage />} />
            <Route path="pasajeros/:status" element={<ProtectedRoute adminOnly><PasajerosPage /></ProtectedRoute>} />
            <Route path="wallet/:gestorId" element={<WalletPage />} />
            <Route path="notificaciones" element={<ProtectedRoute adminOnly><NotificationsPage /></ProtectedRoute>} />
            <Route path="email-settings" element={<ProtectedRoute adminOnly><EmailSettingsPage /></ProtectedRoute>} />
            {/* Admin Client Portal Management */}
            <Route path="admin/analytics" element={<ProtectedRoute adminOnly><AdminAnalyticsPage /></ProtectedRoute>} />
            <Route path="admin/clientes" element={<ProtectedRoute adminOnly><AdminClientsPage /></ProtectedRoute>} />
            <Route path="admin/ordenes-clientes" element={<ProtectedRoute adminOnly><AdminClientOrdersPage /></ProtectedRoute>} />
            <Route path="admin/divisas" element={<ProtectedRoute adminOnly><AdminDivisasPage /></ProtectedRoute>} />
            <Route path="admin/mensajes" element={<ProtectedRoute adminOnly><AdminMessagesPage /></ProtectedRoute>} />
            <Route path="admin/wallet-recargas" element={<ProtectedRoute adminOnly><AdminWalletTopupsPage /></ProtectedRoute>} />
            <Route path="admin/metodos-pago" element={<ProtectedRoute adminOnly><AdminPaymentMethodsPage /></ProtectedRoute>} />
            <Route path="admin/servicios" element={<ProtectedRoute adminOnly><AdminServicesCatalogPage /></ProtectedRoute>} />
            <Route path="admin/tasas" element={<ProtectedRoute adminOnly><AdminExchangeRatesPage /></ProtectedRoute>} />
            <Route path="admin/zona-de-cambios" element={<ProtectedRoute adminOnly><ZonaDeCambiosPage /></ProtectedRoute>} />
            <Route path="admin/auditoria" element={<ProtectedRoute adminOnly><AdminAuditLogPage /></ProtectedRoute>} />
            <Route path="admin/b2b-fees" element={<ProtectedRoute adminOnly><AdminB2BFeesPage /></ProtectedRoute>} />
            <Route path="admin/b2b-orders" element={<ProtectedRoute adminOnly><AdminB2BOrdersPage /></ProtectedRoute>} />
            <Route path="admin/b2b-dashboard" element={<ProtectedRoute adminOnly><AdminB2BDashboard /></ProtectedRoute>} />
            <Route path="admin/recharge-config" element={<ProtectedRoute adminOnly><AdminRechargeConfigPage /></ProtectedRoute>} />
            <Route path="admin/administracion" element={<ProtectedRoute adminOnly><AdminAdministracionPage /></ProtectedRoute>} />
            {/* Workflow Engine */}
            <Route path="workflow" element={<ProtectedRoute adminOnly><WorkflowDashboardPage /></ProtectedRoute>} />
            <Route path="workflow/providers" element={<ProtectedRoute adminOnly><ProvidersPage /></ProtectedRoute>} />
            <Route path="workflow/queues" element={<ProtectedRoute adminOnly><QueuesPage /></ProtectedRoute>} />
            <Route path="workflow/provider-payment-methods" element={<ProtectedRoute adminOnly><AdminProviderPaymentMethodsPage /></ProtectedRoute>} />
            {/* Financial Engine (admin read-only) */}
            <Route path="admin/finance/ledger" element={<ProtectedRoute adminOnly><FinanceLedgerExplorerPage /></ProtectedRoute>} />
            <Route path="admin/finance/trial-balance" element={<ProtectedRoute adminOnly><FinanceTrialBalancePage /></ProtectedRoute>} />
            <Route path="admin/finance/settlements" element={<ProtectedRoute adminOnly><FinanceSettlementsPage /></ProtectedRoute>} />
            <Route path="admin/finance/reconciliations" element={<ProtectedRoute adminOnly><FinanceReconciliationsPage /></ProtectedRoute>} />
            <Route path="admin/finance/snapshots" element={<ProtectedRoute adminOnly><FinanceSnapshotsPage /></ProtectedRoute>} />
            <Route path="admin/recargas" element={<ProtectedRoute adminOnly><AdminRemittanceDepositsPage /></ProtectedRoute>} />
            <Route path="admin/tesoreria-usdt" element={<ProtectedRoute adminOnly><AdminTreasuryUSDTPage /></ProtectedRoute>} />
            {/* CEO Access Hub — unified impersonation panel (gestores + providers) */}
            <Route path="admin/centro-acceso" element={<ProtectedRoute adminOnly><AdminCentroAccesoPage /></ProtectedRoute>} />
            {/* Admin CEO view of gestor remesa modules — reuses gestor components inside admin shell */}
            <Route path="admin/remesas/inicio"        element={<ProtectedRoute adminOnly><GestorRemesasInicioPage /></ProtectedRoute>} />
            <Route path="admin/remesas/conversor"     element={<ProtectedRoute adminOnly><GestorConversorPage /></ProtectedRoute>} />
            <Route path="admin/remesas/enviar"        element={<ProtectedRoute adminOnly><GestorEnviarRemesaPage /></ProtectedRoute>} />
            <Route path="admin/remesas/pendientes"    element={<ProtectedRoute adminOnly><GestorPendientesPage /></ProtectedRoute>} />
            <Route path="admin/remesas/rechazadas"    element={<ProtectedRoute adminOnly><GestorRechazadasPage /></ProtectedRoute>} />
            <Route path="admin/remesas/historial"     element={<ProtectedRoute adminOnly><GestorHistorialPage /></ProtectedRoute>} />
            <Route path="admin/remesas/cuentas"       element={<ProtectedRoute adminOnly><GestorCuentasBancariasPage /></ProtectedRoute>} />
            <Route path="admin/remesas/promotores"    element={<ProtectedRoute adminOnly><GestorPromotoresPage /></ProtectedRoute>} />
            <Route path="admin/remesas/trabajadores"  element={<ProtectedRoute adminOnly><GestorTrabajadoresPage /></ProtectedRoute>} />
            <Route path="admin/remesas/usuarios"      element={<ProtectedRoute adminOnly><GestorUsuariosPage /></ProtectedRoute>} />
            <Route path="admin/remesas/soporte"       element={<ProtectedRoute adminOnly><GestorSoportePage /></ProtectedRoute>} />
            <Route path="admin/remesas/faq"           element={<ProtectedRoute adminOnly><GestorFaqPage /></ProtectedRoute>} />
            {/* Wallets — gestor sees own earnings, admin sees CEO wallet */}
            <Route path="wallet-ganancias"            element={<MyWalletPage />} />
            <Route path="admin/wallet-ceo"            element={<ProtectedRoute adminOnly><MyWalletPage /></ProtectedRoute>} />
            {/* Shared operation detail — visible per role-aware filtering inside the page */}
            <Route path="operations/:id"              element={<OperationDetailPage />} />
            {/* CEO detailed remittance history (USDT-labelled) */}
            <Route path="admin/remesas/historial-detallado" element={<ProtectedRoute adminOnly><RemittanceHistoryDetailedPage /></ProtectedRoute>} />
            <Route path="admin/tasas-cambio" element={<ProtectedRoute adminOnly><ExchangeRatesPanel /></ProtectedRoute>} />
            <Route path="admin/recargas-usdt" element={<ProtectedRoute adminOnly><DepositUsdtSettlementsPage /></ProtectedRoute>} />
            <Route path="admin/resumen-mensual" element={<ProtectedRoute adminOnly><MonthlySummaryPage /></ProtectedRoute>} />
            <Route path="admin/card-bins" element={<ProtectedRoute adminOnly><CardBinManagementPage /></ProtectedRoute>} />
            <Route path="admin/service-pause" element={<ProtectedRoute adminOnly><ServicePausePage /></ProtectedRoute>} />
            <Route path="admin/cleanup-test-data" element={<ProtectedRoute adminOnly><AdminCleanupPage /></ProtectedRoute>} />
            <Route path="admin/gestores-rendicion" element={<ProtectedRoute adminOnly><AdminGestoresRendicionPage /></ProtectedRoute>} />
            <Route path="admin/inicio" element={<ProtectedRoute adminOnly><HomeLandingPage /></ProtectedRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
      </ConnectivityProvider>
      </RealtimeProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
